import { Flex, Box } from '@cad-arc';
import { UnitStatusSearchOptions } from '@mark43/cad-api';
import { UseFormReturn } from 'react-hook-form';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { FormUserMultiSelect } from '../forms/integrated-form-controls/form-user-multi-select';
import { UnitStatusSearchFormType } from './types';
import { UnitStatusSearchFormGeneralFieldWrapper } from './unit-status-search-form-general-field-wrapper';
import { UNIT_TIME_RANGE_FIELDS_HEIGHT } from '../search-windows/constants';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { FormDateRange } from '../forms/integrated-form-controls/form-date-range';
import { useHandleFormDateRange } from '../common/hooks/use-handle-form-date-range';

export function UnitStatusSearchFormGeneralSection({
    form,
    options,
}: {
    form: UseFormReturn<UnitStatusSearchFormType>;
    options: UnitStatusSearchOptions;
}) {
    const { hospitalsEnabled } = useEnabledFeatures();
    const { showDateRangeInput } = useHandleFormDateRange({
        timeRangeOptionPath: 'timeRangeOption',
        dateRangePath: 'dateRange',
    });

    return (
        <Flex alignItems="flex-start" gap={4} flexWrap="wrap">
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="units"
                    label="Units"
                    control={form.control}
                    options={options.callSignOptions}
                    isMultiple
                    truncateSelectedCount={2}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormUserMultiSelect
                    name="personnel"
                    label="Personnel"
                    control={form.control}
                    truncateSelectedCount={2}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="vehicleTags"
                    label="Plate #"
                    control={form.control}
                    options={options.vehicleTagOptions}
                    isMultiple
                    truncateSelectedCount={2}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="dispatchAreas"
                    label="Unit D-Areas"
                    control={form.control}
                    options={options.dispatchAreaOptions}
                    isMultiple
                    truncateSelectedCount={2}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="stations"
                    label="Stations"
                    control={form.control}
                    options={options.stationOptions}
                    isMultiple
                    truncateSelectedCount={2}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                {hospitalsEnabled && (
                    <FormSelect
                        name="hospitals"
                        label="Hospitals"
                        control={form.control}
                        options={options.hospitalOptions}
                        isMultiple
                        truncateSelectedCount={2}
                    />
                )}
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormUserMultiSelect
                    name="transitionedBy"
                    label="Transitioned By"
                    control={form.control}
                    truncateSelectedCount={2}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="agencyProfiles"
                    label="Agencies"
                    control={form.control}
                    options={options.agencyProfileOptions}
                    isMultiple
                    truncateSelectedCount={3}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="agencyType"
                    label="Agency Type"
                    control={form.control}
                    options={options.agencyTypeOptions}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormInput
                    name="narrativeKeywords"
                    label="Narrative Keywords"
                    control={form.control}
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            <UnitStatusSearchFormGeneralFieldWrapper>
                <FormSelect
                    name="timeRangeOption"
                    label="Time Range"
                    control={form.control}
                    options={options?.timeRangeOptions ?? []}
                    isClearable
                />
            </UnitStatusSearchFormGeneralFieldWrapper>
            {showDateRangeInput && (
                <Box
                    marginTop="-4px !important"
                    alignItems="flex-start"
                    height={UNIT_TIME_RANGE_FIELDS_HEIGHT}
                >
                    <FormDateRange
                        name="dateRange"
                        control={form.control}
                        dateRangeProps={{
                            hasCalendar: true,
                            startFormControlProps: { label: 'From' },
                            endFormControlProps: { label: 'To' },
                            granularity: 'minute',
                            hideTimeZone: true,
                            hourCycle: 24,
                        }}
                        rules={{ required: 'Required' }}
                    />
                </Box>
            )}
        </Flex>
    );
}
